import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { 
  Wallet,
  Send,
  Globe,
  CreditCard,
  TrendingUp,
  Shield,
  Zap,
  ChevronRight,
  ChevronLeft,
  X
} from "lucide-react";

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    icon: Zap,
    title: "Welcome to Stelx Pay",
    subtitle: "Modern Payments Infrastructure",
    description: "Move money instantly. Save smart. Grow your wealth - all in one platform.",
    color: "from-teal-500 to-cyan-600"
  },
  {
    id: "wallet",
    icon: Wallet,
    title: "Multi-Currency Wallets",
    subtitle: "JMD, USD, GBP, CAD",
    description: "Hold and manage multiple currencies. Built for global transactions and diaspora connections.",
    color: "from-emerald-500 to-teal-600"
  },
  {
    id: "transfers",
    icon: Send,
    title: "Instant Transfers",
    subtitle: "Send to Anyone, Anywhere",
    description: "P2P transfers to friends, family, or businesses. Use QR codes or email - money moves in seconds.",
    color: "from-cyan-500 to-blue-600"
  },
  {
    id: "remittance",
    icon: Globe,
    title: "Global Remittance",
    subtitle: "Powered by Nium & Stellar",
    description: "Send money home to Jamaica and the Caribbean with competitive rates and fast delivery.",
    color: "from-amber-500 to-orange-600"
  },
  {
    id: "cards",
    icon: CreditCard,
    title: "Virtual Cards",
    subtitle: "Shop Online Securely",
    description: "Create instant virtual cards for online shopping. Control spending limits and freeze cards anytime.",
    color: "from-violet-500 to-purple-600"
  },
  {
    id: "grow",
    icon: TrendingUp,
    title: "Save & Invest",
    subtitle: "AI-Powered Growth",
    description: "Round-up savings, SmartPots for goals, and micro-investing to grow your wealth effortlessly.",
    color: "from-green-500 to-emerald-600"
  },
  {
    id: "security",
    icon: Shield,
    title: "Bank-Grade Security",
    subtitle: "Your Money is Safe",
    description: "Multi-layer encryption, biometric login, and real-time fraud monitoring protect every transaction.",
    color: "from-slate-500 to-zinc-600"
  }
];

const OnboardingModal = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleComplete = () => {
    localStorage.setItem("stelx_onboarding_complete", "true");
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem("stelx_onboarding_complete", "true");
    onComplete();
  };

  if (!isOpen) return null;

  const step = ONBOARDING_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-background/90 backdrop-blur-md"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md glass-heavy rounded-2xl overflow-hidden">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-muted-foreground hover:text-white text-sm font-medium z-10 transition-colors"
        >
          Skip
        </button>

        {/* Content */}
        <div className={`p-8 transition-opacity duration-150 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {step.id === "welcome" ? (
              <img 
                src="/stelx-logo.png" 
                alt="Stelx Pay" 
                className="w-20 h-20 object-contain"
              />
            ) : (
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center`}>
                <Icon className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>
            )}
          </div>

          {/* Text */}
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-2 tracking-tight">
              {step.title}
            </h2>
            <p className="text-primary font-medium text-sm mb-3">
              {step.subtitle}
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
              {step.description}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-6">
            {ONBOARDING_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => !isAnimating && setCurrentStep(index)}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  index === currentStep 
                    ? 'w-6 bg-primary' 
                    : 'w-1.5 bg-white/20 hover:bg-white/30'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <Button
                onClick={handlePrev}
                variant="ghost"
                className="flex-1 h-12 text-muted-foreground hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className={`btn-primary h-12 ${currentStep === 0 ? 'w-full' : 'flex-1'}`}
            >
              {isLastStep ? "Get Started" : "Next"}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
